import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { taxonomyAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '../components/ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Plus, Save, Trash2, ListTree, Link2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

export const TaxonomyPage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [usage, setUsage] = useState({});
  const [activeKey, setActiveKey] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newCatOpen, setNewCatOpen] = useState(false);
  const [newCatLabel, setNewCatLabel] = useState('');
  const [creatingCat, setCreatingCat] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await taxonomyAPI.getAll();
      setCategories(data);
      taxonomyAPI.getUsage().then(setUsage).catch(() => {});
      if (data.length > 0) {
        const fromUrl = searchParams.get('category');
        const initialKey = activeKey || (fromUrl && data.some(c => c.id === fromUrl) ? fromUrl : data[0].id);
        setActiveKey(initialKey);
        setItems((data.find(c => c.id === initialKey) || data[0]).items);
      }
    } catch { toast.error('Failed to load taxonomy'); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const switchTab = (key) => {
    setActiveKey(key);
    const cat = categories.find(c => c.id === key);
    setItems(cat ? cat.items.map(i => ({ ...i })) : []);
  };

  const updateItem = (idx, field, value) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, [field]: value } : it));
  };

  const addItem = () => setItems(prev => [...prev, { value: '', label: '', active: true }]);
  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

  const handleCreateCategory = async () => {
    if (!newCatLabel.trim()) { toast.error('Please enter a name for the new taxonomy'); return; }
    setCreatingCat(true);
    try {
      const created = await taxonomyAPI.createCategory(newCatLabel.trim(), [], user?.name || user?.email);
      setCategories(prev => [...prev, created]);
      setActiveKey(created.id);
      setItems([]);
      setNewCatOpen(false);
      setNewCatLabel('');
      toast.success(`"${created.label}" taxonomy created — add items and Save`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create taxonomy');
    } finally { setCreatingCat(false); }
  };

  const handleSave = async () => {
    if (items.some(i => !i.value.trim() || !i.label.trim())) {
      toast.error('Value and Label cannot be empty'); return;
    }
    setSaving(true);
    try {
      const updated = await taxonomyAPI.updateCategory(activeKey, items, user?.name || user?.email);
      setCategories(prev => prev.map(c => c.id === activeKey ? updated : c));
      toast.success('Taxonomy saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save taxonomy');
    } finally { setSaving(false); }
  };

  const activeCategory = categories.find(c => c.id === activeKey);
  const activeUsage = usage[activeKey] || [];
  const inUse = activeUsage.length > 0;

  const handleDeleteCategory = async () => {
    try {
      await taxonomyAPI.deleteCategory(activeKey);
      const remaining = categories.filter(c => c.id !== activeKey);
      setCategories(remaining);
      setActiveKey(remaining[0]?.id || null);
      setItems(remaining[0]?.items || []);
      setDeleteConfirmOpen(false);
      taxonomyAPI.getUsage().then(setUsage).catch(() => {});
      toast.success('Taxonomy deleted');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete taxonomy');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="taxonomy-page">
      <div>
        <h1 className="text-3xl font-heading font-bold flex items-center gap-2"><ListTree className="w-7 h-7" />Taxonomy Settings</h1>
        <p className="text-muted-foreground">Manage the master dropdown lists used across Content Development</p>
      </div>

      <Card>
        <CardContent className="pt-5">
          <div className="flex items-center justify-between gap-2 flex-wrap border-b border-border pb-4 mb-4">
            <div className="flex items-center gap-2 flex-wrap">
              {loading ? (
                <div className="h-8 w-64 bg-muted animate-pulse rounded" />
              ) : categories.map(c => (
                <button
                  key={c.id}
                  onClick={() => switchTab(c.id)}
                  data-testid={`taxonomy-tab-${c.id}`}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${activeKey === c.id ? 'bg-secondary text-secondary-foreground' : 'text-muted-foreground hover:bg-muted'}`}
                >
                  {c.label}
                  <span className="text-[10px] bg-muted-foreground/10 rounded-full px-1.5">{c.items.length}</span>
                  {(usage[c.id]?.length > 0) && (
                    <span
                      className="text-[10px] font-semibold text-amber-700 bg-amber-100 rounded-full px-1.5 flex items-center gap-0.5"
                      data-testid={`taxonomy-tab-usage-${c.id}`}
                    >
                      <Link2 className="w-2.5 h-2.5" />{usage[c.id].length}
                    </span>
                  )}
                </button>
              ))}
            </div>
            <Button variant="outline" size="sm" onClick={() => setNewCatOpen(true)} data-testid="add-new-taxonomy-btn">
              <Plus className="w-4 h-4 mr-2" />Add New Taxonomy
            </Button>
          </div>

          {activeCategory && (
            <div className="space-y-4" data-testid="taxonomy-category-editor">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-heading font-semibold">{activeCategory.label}</h2>
                <div className="flex items-center gap-2">
                  {!activeCategory.is_core && (
                    inUse ? (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span tabIndex={0} data-testid="taxonomy-delete-disabled-wrap">
                              <Button variant="outline" size="sm" disabled className="text-red-600 opacity-50 cursor-not-allowed pointer-events-none" data-testid="taxonomy-delete-category-btn">
                                <Trash2 className="w-4 h-4 mr-2" />Delete Taxonomy
                              </Button>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent data-testid="taxonomy-delete-tooltip">
                            Detach from {activeUsage.join(', ')} first
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      <Button variant="outline" size="sm" onClick={() => setDeleteConfirmOpen(true)} className="text-red-600 hover:bg-red-50" data-testid="taxonomy-delete-category-btn">
                        <Trash2 className="w-4 h-4 mr-2" />Delete Taxonomy
                      </Button>
                    )
                  )}
                  <Button variant="outline" size="sm" onClick={addItem} data-testid="taxonomy-add-item-btn">
                    <Plus className="w-4 h-4 mr-2" />Add Item
                  </Button>
                  <Button size="sm" onClick={handleSave} disabled={saving} className="bg-lime text-lime-foreground hover:bg-lime/90" data-testid="taxonomy-save-btn">
                    <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </div>

              {inUse ? (
                <div className="flex items-start gap-2 text-sm bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="taxonomy-usage-banner">
                  <Link2 className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                  <span className="text-amber-800">
                    <span className="font-semibold">Used as a custom field on:</span>{' '}
                    {activeUsage.map((e, i) => (
                      <span key={e} className="font-medium">{e}{i < activeUsage.length - 1 ? ', ' : ''}</span>
                    ))}
                    {!activeCategory.is_core && <span className="text-amber-700"> — detach it there before this taxonomy can be deleted.</span>}
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2" data-testid="taxonomy-usage-banner">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span className="text-emerald-800">Not used as a custom field anywhere{!activeCategory.is_core ? ' — safe to delete.' : '.'}</span>
                </div>
              )}

              <div className="grid grid-cols-[1fr_1fr_80px_40px] gap-3 text-xs font-medium text-muted-foreground px-1">
                <span>Value (Internal)</span>
                <span>Label (Display)</span>
                <span>Active</span>
                <span></span>
              </div>
              {items.map((item, idx) => (
                <div key={idx} className="grid grid-cols-[1fr_1fr_80px_40px] gap-3 items-center" data-testid={`taxonomy-item-row-${idx}`}>
                  <Input value={item.value} onChange={e => updateItem(idx, 'value', e.target.value)} data-testid={`taxonomy-item-value-${idx}`} />
                  <Input value={item.label} onChange={e => updateItem(idx, 'label', e.target.value)} data-testid={`taxonomy-item-label-${idx}`} />
                  <Switch checked={item.active} onCheckedChange={v => updateItem(idx, 'active', v)} data-testid={`taxonomy-item-active-${idx}`} />
                  <button onClick={() => removeItem(idx)} className="text-muted-foreground hover:text-red-600 transition-colors" data-testid={`taxonomy-item-delete-${idx}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {items.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">No items yet — click "Add Item" to create one.</p>}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={newCatOpen} onOpenChange={setNewCatOpen}>
        <DialogContent className="max-w-sm" data-testid="add-taxonomy-dialog">
          <DialogHeader>
            <DialogTitle>Add New Taxonomy</DialogTitle>
            <DialogDescription>Creates a new master list that becomes available as an addable field on the Content Item form.</DialogDescription>
          </DialogHeader>
          <div>
            <Label>Taxonomy Name</Label>
            <Input autoFocus placeholder="e.g. Content Owner Team" value={newCatLabel} onChange={e => setNewCatLabel(e.target.value)} data-testid="new-taxonomy-name-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewCatOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateCategory} disabled={creatingCat} className="bg-lime text-lime-foreground hover:bg-lime/90" data-testid="create-taxonomy-btn">
              {creatingCat ? 'Creating...' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{activeCategory?.label}"?</AlertDialogTitle>
            <AlertDialogDescription>This taxonomy and all its items will be permanently removed. Content items already using its values keep them, but it will no longer be selectable.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteCategory} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-taxonomy-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TaxonomyPage;
